import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search,
  DollarSign,
  Calendar,
  CreditCard,
  User,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Printer,
  Building2,
  UsersRound,
  X,
  RefreshCw,
  Landmark,
  Layers,
  ShieldAlert,
  Receipt,
  Info,
  Banknote,
  Percent,
  Check,
  History,
  AlertCircle,
  ChevronRight,
  Phone
} from 'lucide-react';
import StatusBadge from './StatusBadge';
import { creditosAPI, clientesAPI, reportesAPI, describirError } from '../services/api';
import { useToast } from './ToastProvider';
import { useAuth } from '../context/AuthContext';
import { formatoMoneda } from '../utils/finanzas';

const moneda = formatoMoneda;

const fechaHoy = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());
};

const fechaLarga = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).toUpperCase();
};

const FORMAS_PAGO = [
  {
    value: 'EFECTIVO',
    label: 'Efectivo',
    descripcion: 'Pago directo en ventanilla',
    icon: Banknote
  },
  {
    value: 'TRANSFERENCIA',
    label: 'Transferencia / SPEI',
    descripcion: 'Traspaso interbancario o CLABE',
    icon: Landmark
  },
  {
    value: 'DEPOSITO',
    label: 'Depósito Bancario',
    descripcion: 'Ficha en sucursal o practicaja',
    icon: CreditCard
  },
  {
    value: 'OTRO',
    label: 'Otro Medio',
    descripcion: 'Medios especiales autorizados',
    icon: Layers
  }
];

const esTelefonoValido = (tel) => {
  if (!tel) return false;
  const str = String(tel).trim();
  if (!str || str === '—' || str === 'null' || str === 'undefined') return false;
  const soloDigitos = str.replace(/\D/g, '');
  if (soloDigitos.length === 0) return false;
  if (/^0+$/.test(soloDigitos)) return false;
  return true;
};

const mostrarTelefono = (tel) => {
  if (!esTelefonoValido(tel)) return 'Sin teléfono registrado';
  return tel;
};

export default function CajaOperativa({ initialCreditFolio = null }) {
  const toast = useToast();
  const { user, hasPermission, reviewMode } = useAuth();

  // 1. Búsqueda y Selección
  const [busqueda, setBusqueda] = useState(initialCreditFolio || '');
  const [buscando, setBuscando] = useState(false);
  const [resultadosClientes, setResultadosClientes] = useState([]);
  const [resultadosCreditos, setResultadosCreditos] = useState([]);
  
  // 2. Estado de resolución canónica de cliente
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [resolucionCliente, setResolucionCliente] = useState(null);
  const [cargandoResolucion, setCargandoResolucion] = useState(false);
  const [historialModalOpen, setHistorialModalOpen] = useState(false);

  // 3. Crédito actualmente cargado en Caja
  const [creditoSeleccionado, setCreditoSeleccionado] = useState(null);
  const [cargandoCredito, setCargandoCredito] = useState(false);
  const [saldo, setSaldo] = useState(null);
  const [promesas, setPromesas] = useState([]);
  const [pagosCredito, setPagosCredito] = useState([]);
  const [errorCredito, setErrorCredito] = useState(null);

  // 4. Formulario de captura
  // Prelación automática (P08-B0): ya no se elige destino en la captura ordinaria.
  const destino = 'AUTO';
  const [fechaPago, setFechaPago] = useState(fechaHoy());
  const [importeRecibido, setImporteRecibido] = useState('');
  const [formaPago, setFormaPago] = useState('EFECTIVO');
  const [referencia, setReferencia] = useState('');
  const [observaciones, setObservaciones] = useState('');

  // 5. Previsualización en vivo
  const [previa, setPrevia] = useState(null);
  const [previaError, setPreviaError] = useState(null);
  const [previsualizando, setPrevisualizando] = useState(false);

  // 6. Modal de Comprobante
  const [comprobanteModal, setComprobanteModal] = useState(null);

  const abortRef = useRef(null);

  // 7. Registro real del pago (servicio existente de registro y aplicación)
  const [registrando, setRegistrando] = useState(false);
  const registrandoRef = useRef(false); // bloquea doble clic aun antes del re-render
  const idempotencyKeyRef = useRef(null);
  const [ultimoRegistro, setUltimoRegistro] = useState(null);
  const [errorRegistro, setErrorRegistro] = useState(null);

  // Búsqueda inteligente: busca tanto clientes como créditos por folio/legacy/nombre
  useEffect(() => {
    if (!busqueda || busqueda.trim().length < 2) {
      setResultadosClientes([]);
      setResultadosCreditos([]);
      return;
    }

    const q = busqueda.trim();

    const timer = setTimeout(async () => {
      setBuscando(true);
      try {
        const [resCred, resClie] = await Promise.all([
          creditosAPI.listar({ q, limit: 10 }).catch(() => ({ data: { data: [] } })),
          clientesAPI.listar({ q, limit: 10 }).catch(() => ({ data: { data: [] } }))
        ]);
        setResultadosCreditos(resCred.data?.data || []);
        setResultadosClientes(resClie.data?.data || []);
      } catch (err) {
        console.error('Error al buscar en caja:', err);
      } finally {
        setBuscando(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [busqueda]);

  // Cargar contexto financiero de un crédito específico
  const cargarContextoCredito = useCallback(async (creditoId) => {
    setCargandoCredito(true);
    setErrorCredito(null);
    setPrevia(null);
    setPreviaError(null);
    setImporteRecibido('');
    try {
      const [resCred, resSaldo, resProm, resPagos] = await Promise.all([
        creditosAPI.obtener(creditoId),
        creditosAPI.saldo(creditoId),
        creditosAPI.listarPromesas(creditoId).catch(() => ({ data: { promesas: [] } })),
        creditosAPI.listarPagos(creditoId).catch(() => ({ data: { pagos: [] } }))
      ]);

      const cred = resCred.data;
      const sal = resSaldo.data;
      setCreditoSeleccionado(cred);
      setSaldo(sal);
      setPromesas(resProm.data?.promesas || []);
      setPagosCredito(resPagos.data?.pagos || []);

      const titularObj = cred.titular || cred.clienteTitular;
      if (titularObj && !clienteSeleccionado) {
        setClienteSeleccionado(titularObj);
      }
    } catch (err) {
      const e = describirError(err);
      setErrorCredito(e.mensaje);
      toast.error(`Error al cargar situación financiera del crédito: ${e.mensaje}`);
    } finally {
      setCargandoCredito(false);
    }
  }, [toast, clienteSeleccionado]);

  // Resolución Canónica por Cliente (MIDAS Auto-Resolution)
  const seleccionarCliente = useCallback(async (cliente) => {
    setCargandoResolucion(true);
    setClienteSeleccionado(cliente);
    setResultadosClientes([]);
    setResultadosCreditos([]);
    setCreditoSeleccionado(null);
    setSaldo(null);
    setErrorCredito(null);
    setResolucionCliente(null);

    try {
      const res = await clientesAPI.creditoActual(cliente.id);
      const data = res.data;
      setResolucionCliente(data);

      if (data.status === 'AUTO_SELECT_CURRENT_CREDIT' && data.creditoActual) {
        // Auto-selección canónica del crédito actual
        await cargarContextoCredito(data.creditoActual.id);
      }
    } catch (err) {
      const e = describirError(err);
      toast.error(`Error al resolver crédito del cliente: ${e.mensaje}`);
    } finally {
      setCargandoResolucion(false);
    }
  }, [cargarContextoCredito, toast]);

  // Selección directa por Atajo de Folio de Crédito (CR-XXXXXX)
  const seleccionarCreditoDirecto = useCallback(async (credito) => {
    setResultadosCreditos([]);
    setResultadosClientes([]);
    setResolucionCliente(null);

    const clienteId = credito.clienteTitularId || credito.titularId || credito.titular?.id || credito.clienteTitular?.id;
    if (clienteId) {
      clientesAPI.creditoActual(clienteId).then((res) => {
        setResolucionCliente(res.data);
      }).catch(() => {});
    }

    await cargarContextoCredito(credito.id);
  }, [cargarContextoCredito]);

  // Folio inicial si se pasa como prop
  useEffect(() => {
    if (initialCreditFolio) {
      creditosAPI.listar({ q: initialCreditFolio, limit: 1 }).then((res) => {
        const found = res.data?.data?.[0];
        if (found) seleccionarCreditoDirecto(found);
      });
    }
  }, [initialCreditFolio, seleccionarCreditoDirecto]);

  const hayMoratoriosExigibles = Number(saldo?.saldoMoratorios || 0) > 0;


  // Promesa vigente
  const promesaVigente = useMemo(() => {
    return promesas.find((p) => p.estado === 'VIGENTE');
  }, [promesas]);

  // Cálculos contextuales del crédito (Incluso en mora / atraso / 91+ es cobrable)
  const cuotaExigible = useMemo(() => {
    if (saldo?.proximaCuota) {
      return Number(saldo.proximaCuota.importePendiente || saldo.proximaCuota.importeProgramado || 0);
    }
    if (creditoSeleccionado?.importeProximoPago) {
      return Number(creditoSeleccionado.importeProximoPago);
    }
    if (!saldo?.cuotas) return 0;
    const cuotaVencida = saldo.cuotas.find((c) => c.cobertura === 'SIN_PAGO' || c.cobertura === 'PARCIAL');
    return cuotaVencida ? Number(cuotaVencida.importePendiente || cuotaVencida.importeProgramado) : 0;
  }, [saldo, creditoSeleccionado]);

  const { vencimientoMasAntiguo, proximaCuotaPorVencer } = useMemo(() => {
    if (!saldo?.cuotas || saldo.cuotas.length === 0) {
      return {
        vencimientoMasAntiguo: creditoSeleccionado?.proximoVencimiento || null,
        proximaCuotaPorVencer: null
      };
    }
    const hoyISO = fechaHoy();
    const pendientes = saldo.cuotas.filter(
      (c) => Number(c.importePendiente || 0) > 0 || c.cobertura === 'SIN_PAGO' || c.cobertura === 'PARCIAL'
    );
    if (pendientes.length === 0) {
      return { vencimientoMasAntiguo: null, proximaCuotaPorVencer: null };
    }

    const ordenadas = [...pendientes].sort((a, b) => {
      const da = (a.fechaVencimiento instanceof Date ? a.fechaVencimiento.toISOString() : String(a.fechaVencimiento || '')).slice(0, 10);
      const db = (b.fechaVencimiento instanceof Date ? b.fechaVencimiento.toISOString() : String(b.fechaVencimiento || '')).slice(0, 10);
      if (da !== db) return da.localeCompare(db);
      return (a.numeroPago || 0) - (b.numeroPago || 0);
    });

    const masAntigua = ordenadas[0]?.fechaVencimiento || creditoSeleccionado?.proximoVencimiento || null;

    // Próxima cuota por vencer: primera cuota pendiente con fechaVencimiento >= hoyISO
    const porVencer = ordenadas.find((c) => {
      const vctoISO = (c.fechaVencimiento instanceof Date ? c.fechaVencimiento.toISOString() : String(c.fechaVencimiento || '')).slice(0, 10);
      return vctoISO && vctoISO >= hoyISO;
    });

    return {
      vencimientoMasAntiguo: masAntigua,
      proximaCuotaPorVencer: porVencer ? porVencer.fechaVencimiento : null
    };
  }, [saldo, creditoSeleccionado]);

  const diasAtraso = useMemo(() => {
    if (creditoSeleccionado?.diasAtraso !== undefined && creditoSeleccionado?.diasAtraso !== null) {
      return Number(creditoSeleccionado.diasAtraso);
    }
    if (!saldo?.cuotas) return 0;
    const hoyISO = fechaHoy();
    const atrasadas = saldo.cuotas.filter((c) => {
      if (c.cobertura !== 'SIN_PAGO' && c.cobertura !== 'PARCIAL') return false;
      const vctoISO = (c.fechaVencimiento instanceof Date ? c.fechaVencimiento.toISOString() : String(c.fechaVencimiento || '')).slice(0, 10);
      return vctoISO && hoyISO > vctoISO;
    });
    if (atrasadas.length === 0) return 0;
    const primeraISO = (atrasadas[0].fechaVencimiento instanceof Date ? atrasadas[0].fechaVencimiento.toISOString() : String(atrasadas[0].fechaVencimiento || '')).slice(0, 10);
    if (!primeraISO) return 0;
    const [ya, ma, da] = hoyISO.split('-').map(Number);
    const [yb, mb, db] = primeraISO.split('-').map(Number);
    return Math.max(0, Math.round((Date.UTC(ya, ma - 1, da) - Date.UTC(yb, mb - 1, db)) / 86400000));
  }, [saldo, creditoSeleccionado]);

  const { saldoVencidoCalculado, saldoPorVencerCalculado } = useMemo(() => {
    if (creditoSeleccionado?.saldoVencidoReal !== undefined && creditoSeleccionado?.saldoPorVencer !== undefined) {
      return {
        saldoVencidoCalculado: Number(creditoSeleccionado.saldoVencidoReal || 0),
        saldoPorVencerCalculado: Number(creditoSeleccionado.saldoPorVencer || 0)
      };
    }
    if (!saldo?.cuotas) return { saldoVencidoCalculado: 0, saldoPorVencerCalculado: 0 };
    const hoyISO = fechaHoy();
    let vencido = 0;
    let porVencer = 0;
    saldo.cuotas.forEach((c) => {
      const pend = Number(c.importePendiente || 0);
      if (pend > 0) {
        const vctoISO = (c.fechaVencimiento instanceof Date ? c.fechaVencimiento.toISOString() : String(c.fechaVencimiento || '')).slice(0, 10);
        if (vctoISO && hoyISO > vctoISO) {
          vencido += pend;
        } else {
          porVencer += pend;
        }
      }
    });
    vencido += Number(saldo.saldoMoratorios || 0);
    return { saldoVencidoCalculado: vencido, saldoPorVencerCalculado: porVencer };
  }, [saldo, creditoSeleccionado]);

  // Cálculo de Previsualización en Vivo (Soporta pagos parciales en mora / activos)
  useEffect(() => {
    setPrevia(null);
    setPreviaError(null);

    if (!creditoSeleccionado || !importeRecibido) return;

    const num = Number(importeRecibido);
    if (isNaN(num) || num <= 0) return;

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setPrevisualizando(true);

      try {
        const { data } = await creditosAPI.previsualizarPago(
          creditoSeleccionado.id,
          {
            fechaPago,
            importeRecibido: num.toFixed(2)
          },
          { signal: controller.signal }
        );
        setPrevia(data);
        setPreviaError(null);
      } catch (err) {
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
        setPrevia(null);
        setPreviaError(describirError(err));
      } finally {
        setPrevisualizando(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [creditoSeleccionado, fechaPago, importeRecibido]);

  // Un reintento de la MISMA captura reutiliza la llave (el backend responde con replay,
  // sin duplicar); cualquier cambio de la captura inicia un intento nuevo.
  useEffect(() => {
    idempotencyKeyRef.current = null;
  }, [creditoSeleccionado?.id, fechaPago, importeRecibido, formaPago, referencia, observaciones]);

  const confirmarRegistro = async () => {
    if (reviewMode || registrandoRef.current) return;
    if (!creditoSeleccionado || !previa || previaError) return;
    const num = Number(importeRecibido);
    if (isNaN(num) || num <= 0) return;

    registrandoRef.current = true;
    setRegistrando(true);
    setErrorRegistro(null);
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = `ui-caja-${creditoSeleccionado.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    const creditoId = creditoSeleccionado.id;
    const previaConfirmada = previa;
    const captura = { fechaPago, importe: num.toFixed(2), formaPago };
    try {
      const { data } = await creditosAPI.registrarPago(creditoId, {
        fechaPago: captura.fechaPago,
        importeRecibido: captura.importe,
        formaPago: captura.formaPago,
        referencia: referencia || undefined,
        observaciones: observaciones || undefined,
        idempotencyKey: idempotencyKeyRef.current
      });
      const folioPago = data?.pago?.folioPago;
      if (!folioPago) throw new Error('El servidor no confirmó el folio del pago.');

      // Éxito solo con confirmación del backend. Se refresca saldo, historial y efectivo esperado.
      await cargarContextoCredito(creditoId);
      setReferencia('');
      setObservaciones('');
      let efectivoEsperado = null;
      let estadoCaja = null;
      try {
        const mi = await reportesAPI.obtenerMiCierre({ fecha: captura.fechaPago });
        const d = mi.data?.data || mi.data;
        efectivoEsperado = d?.efectivoEsperado ?? null;
        estadoCaja = d?.estado ?? null;
      } catch (_) { /* el pago ya está registrado; el efectivo esperado se consulta en Cierre de Caja */ }
      setUltimoRegistro({ folioPago, importe: captura.importe, formaPago: captura.formaPago, fecha: captura.fechaPago, folioCredito: creditoSeleccionado.folio, efectivoEsperado, estadoCaja });
      toast.exito(`Pago ${folioPago} registrado por ${moneda(captura.importe)}.`);

      const saldoCuotasDesp = data?.saldoCuotas ?? previaConfirmada.saldoCuotasDespues ?? previaConfirmada.saldoCuotas;
      const saldoMoraDesp = data?.saldoMoratorios ?? previaConfirmada.saldoMoratoriosDespues ?? previaConfirmada.saldoMoratorios;
      const saldoTotalDesp = data?.saldoTotalDespues ?? previaConfirmada.saldoTotalDespues ?? previaConfirmada.saldoExigible;
      setComprobanteModal({
        folio: folioPago,
        fecha: captura.fechaPago,
        cliente: titularNombreCompleto,
        folioCredito: creditoSeleccionado.folio,
        importe: captura.importe,
        destino: data?.destino || previaConfirmada.destino || destino,
        forma: FORMAS_PAGO.find((f) => f.value === captura.formaPago)?.label || captura.formaPago,
        aplicaciones: data?.pago?.aplicaciones?.length ? data.pago.aplicaciones : (previaConfirmada.aplicaciones || []),
        saldoCuotasRestante: saldoCuotasDesp,
        saldoMoratoriosRestante: saldoMoraDesp,
        saldoTotalRestante: saldoTotalDesp,
        saldoPosterior: saldoTotalDesp,
        usuario: user?.email || 'operador@midas.local',
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      const e = describirError(err);
      setErrorRegistro(e.mensaje);
      toast.error(`El pago NO se registró: ${e.mensaje}`);
    } finally {
      registrandoRef.current = false;
      setRegistrando(false);
    }
  };

  // Resultado derivado del pago (Sección 13 & Ajuste Semántico)
  const resultadoPago = useMemo(() => {
    if (!previa || !importeRecibido || Number(importeRecibido) <= 0) return null;
    const monto = Number(importeRecibido);

    const saldoCuotasRest = Number(previa.saldoCuotasDespues ?? previa.saldoCuotas ?? 0);
    const saldoMoraRest = Number(
      previa.saldoMoratoriosDespues ??
      previa.saldoMoratorios ??
      (destino === 'MORATORIO' ? Math.max(0, Number(saldo?.saldoMoratorios || 0) - monto) : Number(saldo?.saldoMoratorios || 0))
    );

    // Solo mostrar CRÉDITO TOTALMENTE LIQUIDADO cuando saldo cuotas = 0 Y saldo moratorios = 0
    if (saldoCuotasRest <= 0.005 && saldoMoraRest <= 0.005) {
      return { tipo: 'LIQUIDACION_TOTAL', label: 'Crédito Totalmente Liquidado', badgeClass: 'is-success' };
    }

    if (destino === 'AUTO') {
      if (saldoCuotasRest <= 0.005) {
        return {
          tipo: 'LIQUIDACION_CUOTAS',
          label: saldoMoraRest > 0 ? 'Liquidación de Cuotas (Moratorios pendientes)' : 'Liquidación de Cuotas',
          badgeClass: saldoMoraRest > 0 ? 'is-warning' : 'is-success'
        };
      }
      if (cuotaExigible > 0) {
        if (monto < cuotaExigible - 0.01) {
          return { tipo: 'PARCIAL', label: 'Pago Parcial', badgeClass: 'is-warning' };
        }
        if (Math.abs(monto - cuotaExigible) <= 0.01) {
          return { tipo: 'COMPLETA', label: 'Cuota Completa', badgeClass: 'is-primary' };
        }
        if (monto > cuotaExigible + 0.01) {
          return { tipo: 'ADELANTO', label: 'Adelanto de Cuotas', badgeClass: 'is-info' };
        }
      }
      return { tipo: 'ABONO', label: 'Abono a Cuotas', badgeClass: 'is-primary' };
    }

    if (destino === 'MORATORIO') {
      if (saldoMoraRest <= 0.005) {
        return {
          tipo: 'LIQUIDACION_MORA',
          label: saldoCuotasRest > 0 ? 'Liquidación de Moratorios (Cuotas pendientes)' : 'Liquidación de Moratorios',
          badgeClass: 'is-info'
        };
      }
      return { tipo: 'ABONO_MORA', label: 'Abono a Moratorios', badgeClass: 'is-warning' };
    }

    return null;
  }, [previa, importeRecibido, destino, cuotaExigible, saldo]);

  // Atajos de importe
  const aplicarAtajoImporte = (monto) => {
    if (!creditoSeleccionado) return;
    if (monto !== null && monto !== undefined && Number(monto) > 0) {
      setImporteRecibido(Number(monto).toFixed(2));
    }
  };

  const titularNombreCompleto = clienteSeleccionado?.nombreCompleto
    || creditoSeleccionado?.titular?.nombreCompleto
    || (creditoSeleccionado?.clienteTitular
      ? `${creditoSeleccionado.clienteTitular.nombres} ${creditoSeleccionado.clienteTitular.apellidoPaterno} ${creditoSeleccionado.clienteTitular.apellidoMaterno || ''}`.trim()
      : (clienteSeleccionado ? `${clienteSeleccionado.nombres} ${clienteSeleccionado.apellidoPaterno} ${clienteSeleccionado.apellidoMaterno || ''}`.trim() : 'Sin cliente seleccionado'));

  const clienteFolio = clienteSeleccionado?.folio || creditoSeleccionado?.titular?.folio || creditoSeleccionado?.clienteTitular?.folio || 'CL-—';
  const clienteTelefono = clienteSeleccionado?.telefonoPrincipal || creditoSeleccionado?.titular?.telefonoPrincipal || creditoSeleccionado?.clienteTitular?.telefonoPrincipal || '—';

  const resetearSeleccion = () => {
    setCreditoSeleccionado(null);
    setClienteSeleccionado(null);
    setResolucionCliente(null);
    setSaldo(null);
    setBusqueda('');
    setPrevia(null);
  };

  // Normalización de Modalidad para CR-005963 y Grupos Pseudo-General
  const esGrupoGeneral = (creditoSeleccionado?.nombreGrupo && (creditoSeleccionado.nombreGrupo.includes('GENERAL') || creditoSeleccionado.nombreGrupo.trim() === '1 - GENERAL')) ||
                         (creditoSeleccionado?.agrupacion?.nombre && (creditoSeleccionado.agrupacion.nombre.includes('GENERAL') || creditoSeleccionado.agrupacion.nombre.trim() === '1 - GENERAL'));
  
  const modalidadVisible = (creditoSeleccionado?.tipoCredito === 'GRUPAL' && esGrupoGeneral)
    ? 'INDIVIDUAL'
    : (creditoSeleccionado?.tipoCreditoCanonico || (creditoSeleccionado?.tipoCredito === 'PERSONAL' ? 'INDIVIDUAL' : creditoSeleccionado?.tipoCredito));

  const tieneClienteActivo = Boolean(clienteSeleccionado || creditoSeleccionado);
  const esMultiple = resolucionCliente?.status === 'MULTIPLE_CURRENT_CREDITS';
  const esZero = resolucionCliente?.status === 'ZERO_CURRENT_CREDITS';
  const cajaHabilitada = Boolean(creditoSeleccionado && saldo && !esMultiple && !esZero);

  return (
    <div className="caja-operativa-wrapper">
      {/* 1. Barra de Búsqueda Superior (Franja de Selección) */}
      <div className="caja-search-card">
        <div className="caja-search-header">
          <div className="caja-search-title">
            <Landmark size={20} className="text-primary" />
            <div>
              <h3>Caja Operativa · Recepción de Pagos</h3>
              <p>Busca al cliente por nombre, folio (CL-...) o teléfono. (Atajo por folio de crédito: CR-...).</p>
            </div>
          </div>
          {tieneClienteActivo && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={resetearSeleccion}
            >
              <X size={14} />
              <span>Cambiar Cliente</span>
            </button>
          )}
        </div>

        <div className="caja-search-input-box">
          <div className="search-box">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              id="caja-credit-search"
              placeholder="Buscar cliente por nombre, folio CL o teléfono (o CR exacto)..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoComplete="off"
            />
            {buscando && <RefreshCw size={16} className="spin search-spinner" />}
            {busqueda && !buscando && (
              <button type="button" className="search-clear" onClick={() => setBusqueda('')}>
                <X size={14} />
              </button>
            )}
          </div>

          {/* Resultados de búsqueda de CLIENTES */}
          {resultadosClientes.length > 0 && (
            <div className="caja-search-results-dropdown">
              <div className="results-count-label">
                {resultadosClientes.length} cliente(s) encontrado(s) — Selecciona para cargar su Caja:
              </div>
              {resultadosClientes.map((c) => (
                <div
                  key={c.id}
                  className="search-result-item"
                  onClick={() => seleccionarCliente(c)}
                >
                  <div className="result-main-col">
                    <span className="result-folio font-mono">{c.folio}</span>
                    <span className="result-cliente">{c.nombreCompleto || `${c.nombres} ${c.apellidoPaterno} ${c.apellidoMaterno || ''}`.trim()}</span>
                  </div>
                  <div className="result-badges-col">
                    <span className={`result-tipo-badge ${!esTelefonoValido(c.telefonoPrincipal) ? 'is-no-phone text-muted' : ''}`}>
                      <Phone size={11} /> {mostrarTelefono(c.telefonoPrincipal)}
                    </span>
                    <StatusBadge value={c.estado} />
                    <ChevronRight size={16} className="text-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Resultados de atajo directo de CRÉDITOS */}
          {resultadosCreditos.length > 0 && (
            <div className="caja-search-results-dropdown">
              <div className="results-count-label">
                {resultadosCreditos.length} crédito(s) coincidente(s):
              </div>
              {resultadosCreditos.map((c) => (
                <div
                  key={c.id}
                  className="search-result-item"
                  onClick={() => seleccionarCreditoDirecto(c)}
                >
                  <div className="result-main-col">
                    <span className="result-folio font-mono">{c.folio}</span>
                    <span className="result-cliente">
                      {c.titular?.nombreCompleto
                        || (c.clienteTitular
                          ? `${c.clienteTitular.nombres} ${c.clienteTitular.apellidoPaterno} ${c.clienteTitular.apellidoMaterno || ''}`.trim()
                          : c.clienteNombre || 'Sin cliente titular')}
                    </span>
                  </div>
                  <div className="result-badges-col">
                    <span className="result-tipo-badge">{c.tipoCredito}</span>
                    <StatusBadge value={c.estado} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 2. Estructura Permanente de Caja Operativa (Siempre Visible) */}
      <div className={`caja-main-grid ${!cajaHabilitada ? 'is-disabled-shell' : ''}`}>
        {/* Panel Izquierdo: Identificación, Cuenta y Captura de Pago */}
        <div className="caja-left-panel">
          {/* A. Bloque de Identificación del Cliente y Cuenta */}
          <div className="caja-context-card">
            {cargandoResolucion || cargandoCredito ? (
              <div className="caja-inline-loading">
                <RefreshCw size={18} className="spin text-primary" />
                <span>Resolviendo situación financiera y cuenta cobrable del cliente...</span>
              </div>
            ) : !tieneClienteActivo ? (
              <div className="caja-no-client-prompt">
                <User size={22} className="text-muted" />
                <div>
                  <h4>Selecciona un cliente para iniciar la recepción del pago</h4>
                  <p>Utiliza el buscador superior por nombre, folio CL- o teléfono para abrir la caja operativa.</p>
                </div>
              </div>
            ) : (
              <>
                {errorCredito && (
                  <div className="previa-error-box mb-3">
                    <AlertTriangle size={18} />
                    <span>{errorCredito}</span>
                  </div>
                )}
                <div className="context-header">
                  <div className="context-id">
                    {creditoSeleccionado ? (
                      <>
                        <span className="context-folio font-mono">{creditoSeleccionado.folio}</span>
                        <StatusBadge value={creditoSeleccionado.estado} />
                        <span className="context-modalidad">{modalidadVisible}</span>
                        {creditoSeleccionado.productoVersion && !creditoSeleccionado.productoReferencial && (
                          <span className="context-product-badge">
                            {creditoSeleccionado.productoVersion.producto?.codigo || creditoSeleccionado.productoVersion.codigo || 'CRÉDITO'}
                          </span>
                        )}
                        {creditoSeleccionado.frecuencia && (
                          <span className="context-product-badge">
                            {creditoSeleccionado.frecuencia}
                          </span>
                        )}
                        {(!esGrupoGeneral && (creditoSeleccionado.grupoNombre || creditoSeleccionado.colocadoraNombre || (creditoSeleccionado.agrupacion && !creditoSeleccionado.agrupacion.nombre?.includes('GENERAL') && creditoSeleccionado.agrupacion.nombre?.trim() !== '1 - GENERAL'))) && (
                          <span className="context-agrupacion">
                            {creditoSeleccionado.tipoCredito === 'COLOCADORA' ? <Building2 size={13} /> : <UsersRound size={13} />}
                            {creditoSeleccionado.colocadoraNombre || creditoSeleccionado.grupoNombre || creditoSeleccionado.agrupacion?.nombre}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="context-folio text-muted">Sin crédito seleccionado</span>
                    )}
                  </div>

                  <div className="context-client">
                    <User size={16} />
                    <span className="client-name">{titularNombreCompleto}</span>
                    <span className="client-folio font-mono">({clienteFolio})</span>
                    <span className={`client-phone font-mono ${!esTelefonoValido(clienteTelefono) ? 'is-empty text-muted' : ''}`}>
                      <Phone size={12} /> {mostrarTelefono(clienteTelefono)}
                    </span>
                  </div>
                </div>

                {/* Aviso inline si se detectó Excepción de Múltiples Créditos Cobrables */}
                {esMultiple && (
                  <div className="caja-inline-warning mt-2">
                    <div className="flex items-start gap-2">
                      <ShieldAlert size={18} className="text-danger flex-shrink-0 mt-0.5" />
                      <div>
                        <strong>Revisión requerida para determinar la cuenta destino.</strong>
                        <p>{resolucionCliente.mensaje}</p>
                      </div>
                    </div>

                    <div className="multiple-credits-table-box mt-2">
                      <table className="caja-table-warning">
                        <thead>
                          <tr>
                            <th>Folio</th>
                            <th>Modalidad</th>
                            <th>Estado</th>
                            <th>Saldo Cuotas</th>
                            <th>Moratorios</th>
                            <th>Saldo Total</th>
                            <th>Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resolucionCliente.creditosCobrables?.map((c) => (
                            <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => cargarContextoCredito(c.id)}>
                              <td className="font-mono font-bold text-primary">{c.folio}</td>
                              <td>{c.tipoCreditoCanonico || c.tipoCredito}</td>
                              <td><StatusBadge value={c.estado} /></td>
                              <td className="font-mono">{moneda(c.saldoCuotas)}</td>
                              <td className="font-mono">{moneda(c.saldoMoratorios)}</td>
                              <td className="font-mono font-bold">{moneda(c.saldoActual)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-primary btn-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cargarContextoCredito(c.id);
                                  }}
                                >
                                  Seleccionar
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Aviso inline si el cliente tiene Cero créditos cobrables */}
                {esZero && (
                  <div className="caja-inline-warning is-zero mt-2">
                    <AlertCircle size={18} className="text-muted flex-shrink-0" />
                    <div>
                      <strong>Cuenta al corriente / Sin saldo pendiente</strong>
                      <p>{resolucionCliente.mensaje}</p>
                    </div>
                  </div>
                )}

                {/* Enlace discreto a Historial de Créditos */}
                {resolucionCliente?.creditosHistoricos?.length > 0 && (
                  <div className="caja-history-link-bar">
                    <button
                      type="button"
                      className="btn-link-history"
                      onClick={() => setHistorialModalOpen(true)}
                    >
                      <History size={13} />
                      <span>Ver historial de créditos ({resolucionCliente.creditosHistoricos.length} anteriores en consulta)</span>
                    </button>
                  </div>
                )}

                {/* Banner de Promesa Vigente si existe */}
                {promesaVigente && (
                  <div className="caja-promesa-banner mt-2">
                    <Clock size={18} className="promesa-icon" />
                    <div className="promesa-text">
                      <span className="promesa-tag">PROMESA DE PAGO VIGENTE</span>
                      <span className="promesa-detail">
                        Compromiso de <strong>{moneda(promesaVigente.importePrometido)}</strong> para el <strong>{fechaLarga(promesaVigente.fechaPromesa)}</strong>.
                        <em className="promesa-note"> (Informativo: no altera el destino del pago automáticamente)</em>
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Atajos Rápidos Canónicos */}
            <div className="caja-shortcuts-bar">
              <span className="shortcuts-title">Sugerencias de cobro:</span>
              <div className="shortcuts-buttons">
                {cuotaExigible > 0 && (
                  <button
                    type="button"
                    className="btn-shortcut"
                    disabled={!cajaHabilitada}
                    onClick={() => aplicarAtajoImporte(cuotaExigible)}
                  >
                    Pagar Cuota ({moneda(cuotaExigible)})
                  </button>
                )}
                {Number(saldo?.saldoExigible || 0) > 0 && (
                  <button
                    type="button"
                    className="btn-shortcut"
                    disabled={!cajaHabilitada}
                    onClick={() => aplicarAtajoImporte(saldo.saldoExigible)}
                  >
                    {hayMoratoriosExigibles ? `Liquidar Crédito con moratorios (${moneda(saldo.saldoExigible)})` : `Liquidar Crédito (${moneda(saldo.saldoExigible)})`}
                  </button>
                )}
                {!cajaHabilitada && (
                  <span className="text-xs text-muted">Selecciona un crédito cobrable para habilitar atajos.</span>
                )}
              </div>
              {hayMoratoriosExigibles && Number(saldo?.saldoCuotas || 0) > 0 && (
                <div className="shortcuts-multidestino-notice">
                  <Info size={13} />
                  <span>El pago se aplica solo: cada cuota vencida y su moratorio se cubren antes de las cuotas siguientes.</span>
                </div>
              )}
            </div>
          </div>

          {/* B. Formulario de Captura de Pago */}
          <div className="caja-form-card">
            <div className="caja-card-head">
              <DollarSign size={18} />
              <h4>Captura de Pago en Caja</h4>
            </div>

            <div className="caja-form-grid">
              {/* 1. Aplicación automática (P08-B0): el cajero no elige destino */}
              <div className="form-group col-span-2">
                <label className="form-label-bold">Aplicación del pago</label>
                <div className="shortcuts-multidestino-notice">
                  <Info size={13} />
                  <span>
                    Automática: primero la cuota vencida más antigua, después su moratorio y luego las siguientes cuotas.
                    {saldo ? ` Cuotas pendientes: ${moneda(saldo.saldoCuotas)} · Moratorio pendiente: ${moneda(saldo.saldoMoratorios)}.` : ''}
                  </span>
                </div>
              </div>

              {/* 2. Importe Recibido y Fecha */}
              <div className="form-group">
                <label htmlFor="caja-importe-recibido" className="form-label-bold">Importe Recibido ($)</label>
                <div className="input-with-icon is-large">
                  <DollarSign size={20} className="input-icon" />
                  <input
                    id="caja-importe-recibido"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    value={importeRecibido}
                    onChange={(e) => setImporteRecibido(e.target.value)}
                    className="filter-input caja-input-amount"
                    disabled={!cajaHabilitada}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="caja-fecha-pago" className="form-label-bold">Fecha del Pago</label>
                <div className="input-with-icon">
                  <Calendar size={16} className="input-icon" />
                  <input
                    id="caja-fecha-pago"
                    type="date"
                    value={fechaPago}
                    onChange={(e) => setFechaPago(e.target.value)}
                    className="filter-input"
                    disabled={!cajaHabilitada}
                  />
                </div>
              </div>

              {/* 3. Forma de Pago (Selector Visual Profesional) */}
              <div className="form-group col-span-2">
                <label className="form-label-bold">Forma de Pago</label>
                <div className="forma-pago-grid">
                  {FORMAS_PAGO.map((f) => {
                    const IconComponent = f.icon;
                    const selected = formaPago === f.value;
                    return (
                      <button
                        key={f.value}
                        type="button"
                        className={`btn-forma-card ${selected ? 'is-selected' : ''}`}
                        onClick={() => setFormaPago(f.value)}
                        disabled={!cajaHabilitada}
                      >
                        <div className="forma-card-top">
                          <div className="forma-icon-box">
                            <IconComponent size={18} />
                          </div>
                          {selected && (
                            <div className="forma-check-badge">
                              <Check size={12} strokeWidth={3} />
                            </div>
                          )}
                        </div>
                        <span className="forma-name">{f.label}</span>
                        <span className="forma-desc">{f.descripcion}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Campos Condicionales según Forma de Pago */}
              {formaPago === 'TRANSFERENCIA' && (
                <div className="form-group col-span-2 bank-fields-group">
                  <div className="bank-fields-header">
                    <Landmark size={15} />
                    <span>Datos de Transferencia / SPEI</span>
                  </div>
                  <div className="bank-fields-inputs">
                    <div className="form-group">
                      <label htmlFor="caja-referencia">
                        Referencia / folio bancario <span className="text-danger">*</span>
                      </label>
                      <input
                        id="caja-referencia"
                        type="text"
                        maxLength={120}
                        placeholder="Ej: SPEI-7489201, Clave de rastreo..."
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="caja-observaciones">Banco Origen / Cuenta / Notas (Opcional)</label>
                      <input
                        id="caja-observaciones"
                        type="text"
                        maxLength={500}
                        placeholder="Ej: BBVA Cta. 4829, Sucursal Centro..."
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                      />
                    </div>
                  </div>
                </div>
              )}

              {formaPago === 'DEPOSITO' && (
                <div className="form-group col-span-2 bank-fields-group">
                  <div className="bank-fields-header">
                    <CreditCard size={15} />
                    <span>Datos de Depósito Bancario</span>
                  </div>
                  <div className="bank-fields-inputs">
                    <div className="form-group">
                      <label htmlFor="caja-referencia">
                        Referencia de depósito <span className="text-danger">*</span>
                      </label>
                      <input
                        id="caja-referencia"
                        type="text"
                        maxLength={120}
                        placeholder="Ej: Ficha #104829, Folio Practicaja..."
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="caja-observaciones">Banco / Sucursal / Notas (Opcional)</label>
                      <input
                        id="caja-observaciones"
                        type="text"
                        maxLength={500}
                        placeholder="Ej: Banamex Suc. 120, Ventanilla 2..."
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                      />
                    </div>
                  </div>
                </div>
              )}

              {formaPago === 'OTRO' && (
                <div className="form-group col-span-2 bank-fields-group">
                  <div className="bank-fields-header">
                    <Layers size={15} />
                    <span>Descripción de Otro Medio de Pago</span>
                  </div>
                  <div className="bank-fields-inputs">
                    <div className="form-group">
                      <label htmlFor="caja-referencia">
                        Descripción del medio de pago <span className="text-danger">*</span>
                      </label>
                      <input
                        id="caja-referencia"
                        type="text"
                        maxLength={120}
                        placeholder="Especifica el medio utilizado (Ej: Cheque de caja #, Nómina)..."
                        value={referencia}
                        onChange={(e) => setReferencia(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="caja-observaciones">Detalles u Observaciones Adicionales</label>
                      <input
                        id="caja-observaciones"
                        type="text"
                        maxLength={500}
                        placeholder="Detalles de validación o folio de autorización..."
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        className="filter-input"
                        disabled={!cajaHabilitada}
                      />
                    </div>
                  </div>
                </div>
              )}

              {formaPago === 'EFECTIVO' && (
                <div className="form-group col-span-2">
                  <label htmlFor="caja-observaciones">Observaciones de Caja (Opcional)</label>
                  <input
                    id="caja-observaciones"
                    type="text"
                    maxLength={500}
                    placeholder="Comentarios sobre la operación en ventanilla..."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    className="filter-input"
                    disabled={!cajaHabilitada}
                  />
                </div>
              )}

              {/* Sección informativa de Ajustes Autorizados */}
              <div className="form-group col-span-2 caja-discounts-notice">
                <div className="notice-icon-box">
                  <Percent size={14} />
                </div>
                <span>
                  <strong>Ajustes y Condonaciones:</strong> No disponibles en captura directa de caja. Toda condonación de moratorios debe gestionarse con perfil autorizado en el módulo de Moratorios.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Panel Derecho: Estado de Cuenta (Saldos), Preview Canónico y Confirmación */}
        <div className="caja-right-panel">
          {/* C. Grilla de Saldos Canónicos del Estado de Cuenta */}
          <div className="caja-balances-card">
            <div className="caja-balances-grid">
              <div className="balance-box is-total">
                <span className="balance-label">SALDO TOTAL</span>
                <span className="balance-amount">{saldo ? moneda(saldo.saldoExigible || (Number(saldo.saldoCuotas || 0) + Number(saldo.saldoMoratorios || 0))) : '$0.00'}</span>
                <span className="balance-subtext">Cuotas + Moratorios pendientes</span>
              </div>
              <div className="balance-box is-cuota">
                <span className="balance-label">Saldo de Cuotas</span>
                <span className="balance-amount">{saldo ? moneda(saldo.saldoCuotas || 0) : '$0.00'}</span>
                <span className="balance-subtext">Componente contractual pendiente</span>
              </div>
              <div className="balance-box is-moratorio">
                <span className="balance-label">Moratorios Pendientes</span>
                <span className="balance-amount">{saldo ? moneda(saldo.saldoMoratorios || 0) : '$0.00'}</span>
                <span className="balance-subtext">Recargos por atraso</span>
              </div>
              <div className="balance-box is-cuota-exigible">
                <span className="balance-label">Cuota Exigible</span>
                <span className="balance-amount">{saldo ? moneda(cuotaExigible) : '$0.00'}</span>
                <span className="balance-subtext">Importe exigible inmediato</span>
              </div>
            </div>

            {/* Metadata financiera secundaria */}
            <div className="caja-meta-bar">
              <div className="meta-item">
                <span className="meta-lbl">Saldo Vencido:</span>
                <strong className="meta-val font-mono">{moneda(saldoVencidoCalculado)}</strong>
              </div>
              <div className="meta-item">
                <span className="meta-lbl">Saldo por Vencer:</span>
                <strong className="meta-val font-mono">{moneda(saldoPorVencerCalculado)}</strong>
              </div>
              <div className="meta-item">
                <span className="meta-lbl">Vencimiento más antiguo pendiente:</span>
                <span className="meta-val font-bold">{fechaLarga(vencimientoMasAntiguo)}</span>
              </div>
              {proximaCuotaPorVencer ? (
                <div className="meta-item">
                  <span className="meta-lbl">Próxima cuota por vencer:</span>
                  <span className="meta-val font-medium">{fechaLarga(proximaCuotaPorVencer)}</span>
                </div>
              ) : (
                <div className="meta-item">
                  <span className="meta-lbl">Próxima cuota por vencer:</span>
                  <span className="meta-val text-muted">Sin cuotas futuras</span>
                </div>
              )}
              {diasAtraso > 0 && (
                <div className="meta-item is-danger">
                  <span className="meta-lbl">Atraso:</span>
                  <span className="meta-val font-bold">{diasAtraso} día(s)</span>
                </div>
              )}
            </div>
          </div>

          {/* D. Simulación Canónica de Aplicación (Live Preview) */}
          <div className="caja-preview-card">
            <div className="caja-card-head">
              <Receipt size={18} />
              <h4>Simulación Canónica de Aplicación</h4>
              {previsualizando && <RefreshCw size={14} className="spin text-primary" />}
            </div>

            {previaError && (
              <div className="previa-error-box">
                <AlertTriangle size={18} />
                <span>{previaError.mensaje || 'Error al simular aplicación del pago'}</span>
              </div>
            )}

            {!cajaHabilitada ? (
              <div className="previa-placeholder is-compact">
                <ShieldAlert size={24} strokeWidth={1.5} className="text-muted" />
                <p>Selecciona un cliente y cuenta cobrable para simular la distribución financiera del pago.</p>
              </div>
            ) : !importeRecibido || Number(importeRecibido) <= 0 ? (
              <div className="previa-placeholder is-compact">
                <div className="previa-empty-badge">
                  <DollarSign size={18} className="text-primary" />
                  <span>Esperando importe</span>
                </div>
                <p className="text-xs text-muted">Ingresa un monto o selecciona un atajo para simular la distribución canónica en tiempo real.</p>
              </div>
            ) : previa ? (
              <div className="previa-content">
                {/* Badge de Resultado Derivado */}
                {resultadoPago && (
                  <div className="previa-result-banner">
                    <span className="result-label">Resultado Estimado:</span>
                    <span className={`result-tag ${resultadoPago.badgeClass}`}>
                      {resultadoPago.label}
                    </span>
                  </div>
                )}

                {/* Desglose de Aplicación Estimada */}
                <div className="previa-section">
                  <span className="previa-section-title">
                    Aplicación {previa.destino === 'MORATORIO' ? 'a cargos moratorios' : 'automática (cuotas y moratorios)'}:
                  </span>
                  <div className="previa-table-container">
                    <table className="previa-table">
                      <thead>
                        <tr>
                          <th>Concepto</th>
                          <th>Vencimiento</th>
                          <th className="text-right">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previa.aplicaciones?.map((a, i) => (
                          <tr key={i}>
                            <td className="font-semibold">
                              {a.tipo === 'MORATORIO'
                                ? `Moratorio cuota #${a.numeroPago}`
                                : `Cuota #${a.numeroPago}`}
                            </td>
                            <td className="text-muted">{fechaLarga(a.fechaVencimiento)}</td>
                            <td className="text-right font-mono font-bold text-success">
                              {moneda(a.importeAplicado)}
                            </td>
                          </tr>
                        ))}
                        {(!previa.aplicaciones || previa.aplicaciones.length === 0) && (
                          <tr>
                            <td colSpan={3} className="text-center text-muted">
                              Sin aplicaciones calculadas
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Comparativo de Saldos: Antes vs Después */}
                <div className="previa-comparison-box">
                  <div className="comparison-row is-total-row">
                    <span>SALDO TOTAL RESTANTE:</span>
                    <div className="comparison-vals">
                      <span className="text-muted line-through">
                        {moneda(previa.saldoTotalAntes ?? (Number(saldo.saldoCuotas || 0) + Number(saldo.saldoMoratorios || 0)))}
                      </span>
                      <span>→</span>
                      <strong className="text-total font-mono">
                        {moneda(previa.saldoTotalDespues ?? (Number(previa.saldoCuotasDespues ?? previa.saldoCuotas ?? 0) + Number(previa.saldoMoratoriosDespues ?? previa.saldoMoratorios ?? 0)))}
                      </strong>
                    </div>
                  </div>

                  <div className="comparison-row is-sub-row">
                    <span>Saldo de Cuotas:</span>
                    <div className="comparison-vals font-mono">
                      <span className="text-muted">{moneda(previa.saldoCuotasAntes ?? saldo.saldoCuotas)}</span>
                      <span>→</span>
                      <strong>{moneda(previa.saldoCuotasDespues ?? previa.saldoCuotas)}</strong>
                    </div>
                  </div>

                  <div className="comparison-row is-sub-row">
                    <span>Moratorios Pendientes:</span>
                    <div className="comparison-vals font-mono">
                      <span className="text-muted">{moneda(previa.saldoMoratoriosAntes ?? saldo.saldoMoratorios)}</span>
                      <span>→</span>
                      <strong>{moneda(previa.saldoMoratoriosDespues ?? previa.saldoMoratorios)}</strong>
                    </div>
                  </div>
                </div>

                {/* Acciones de Confirmación y Comprobante */}
                <div className="caja-confirm-section">
                  {reviewMode && (
                    <div className="read-only-notice-banner">
                      <ShieldAlert size={18} />
                      <div className="notice-text">
                        <strong>MODO REVISIÓN ACTIVO</strong>
                        <p>El backend protege la cartera en READ-ONLY. La confirmación de pagos reales está bloqueada.</p>
                      </div>
                    </div>
                  )}

                  <div className="caja-actions-grid">
                    <button
                      type="button"
                      className="btn btn-primary btn-block btn-lg"
                      disabled={reviewMode || registrando || !previa || !!previaError || !hasPermission('PAGOS_REGISTRAR')}
                      title={
                        reviewMode
                          ? "Modo revisión: las escrituras están deshabilitadas."
                          : !hasPermission('PAGOS_REGISTRAR')
                          ? "Permiso insuficiente para registrar pagos"
                          : "Confirmar y registrar pago en caja"
                      }
                      onClick={confirmarRegistro}
                    >
                      <CheckCircle2 size={18} />
                      <span>{reviewMode ? 'Confirmar Pago (Bloqueado en Read-Only)' : registrando ? 'Registrando pago...' : 'Confirmar y Registrar Pago'}</span>
                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary btn-block"
                      onClick={() => {
                        const saldoCuotasDesp = previa.saldoCuotasDespues || previa.saldoCuotas;
                        const saldoMoraDesp = previa.saldoMoratoriosDespues || previa.saldoMoratorios;
                        const saldoTotalDesp = previa.saldoTotalDespues || previa.saldoExigible || (Number(saldoCuotasDesp) + Number(saldoMoraDesp)).toFixed(2);

                        setComprobanteModal({
                          folio: 'PREVIEW-RECIBO',
                          fecha: fechaPago,
                          cliente: titularNombreCompleto,
                          folioCredito: creditoSeleccionado.folio,
                          importe: importeRecibido,
                          destino: previa.destino || destino,
                          forma: FORMAS_PAGO.find(f => f.value === formaPago)?.label || formaPago,
                          aplicaciones: previa.aplicaciones || [],
                          saldoCuotasRestante: saldoCuotasDesp,
                          saldoMoratoriosRestante: saldoMoraDesp,
                          saldoTotalRestante: saldoTotalDesp,
                          saldoPosterior: saldoTotalDesp,
                          usuario: user?.email || 'operador@midas.local',
                          timestamp: new Date().toISOString()
                        });
                      }}
                    >
                      <Printer size={16} />
                      <span>Previsualizar Comprobante</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {(ultimoRegistro || errorRegistro) && (
            <div
              role="status"
              data-testid="caja-resultado-registro"
              style={{
                border: `1px solid ${errorRegistro ? '#fca5a5' : '#86efac'}`,
                background: errorRegistro ? '#fef2f2' : '#f0fdf4',
                borderRadius: 8,
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                fontSize: '0.9rem'
              }}
            >
              {errorRegistro ? (
                <span><strong>El pago NO se registró.</strong> {errorRegistro}</span>
              ) : (
                <span>
                  <strong>Pago registrado: {ultimoRegistro.folioPago}</strong> · {moneda(ultimoRegistro.importe)} ·{' '}
                  {FORMAS_PAGO.find((f) => f.value === ultimoRegistro.formaPago)?.label || ultimoRegistro.formaPago} ·{' '}
                  {ultimoRegistro.folioCredito}
                  {ultimoRegistro.efectivoEsperado != null && (
                    <> · Efectivo esperado de tu caja ({ultimoRegistro.fecha}): <strong>{moneda(ultimoRegistro.efectivoEsperado)}</strong>{ultimoRegistro.estadoCaja ? ` (${ultimoRegistro.estadoCaja})` : ''}</>
                  )}
                </span>
              )}
            </div>
          )}

          {/* E. Historial Reciente de Pagos de la Cuenta */}
          <div className="caja-history-card">
            <div className="caja-card-head">
              <Clock size={16} />
              <h4>Historial de Pagos de este Crédito</h4>
            </div>

            {pagosCredito.length === 0 ? (
              <p className="empty-history-text">Sin pagos previos registrados.</p>
            ) : (
              <div className="caja-mini-history">
                {pagosCredito.slice(0, 5).map((p) => (
                  <div
                    key={p.id}
                    className={`mini-history-item ${p.estado === 'REVERSADO' ? 'is-reversado' : ''}`}
                  >
                    <div className="item-left">
                      <span className="item-folio font-mono">{p.folioPago}</span>
                      <span className="item-date">{fechaLarga(p.fechaPago)}</span>
                      <span className={`destino-badge is-${p.destino?.toLowerCase() || 'cuota'}`}>
                        {p.destino || 'CUOTA'}
                      </span>
                    </div>
                    <div className="item-right">
                      <span className="item-amount font-mono">{moneda(p.importeRecibido)}</span>
                      <StatusBadge value={p.estado} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Secundario de HISTORIAL DE CRÉDITOS DEL CLIENTE (Sólo Lectura) */}
      {historialModalOpen && (
        <div className="modal-overlay" onClick={() => setHistorialModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
            <div className="modal-header">
              <div className="flex items-center gap-2">
                <History size={18} className="text-primary" />
                <h3>Historial de Créditos del Cliente</h3>
              </div>
              <button className="modal-close" onClick={() => setHistorialModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body p-4">
              <p className="text-muted text-sm mb-3">
                Consulta histórica de créditos anteriores de <strong>{titularNombreCompleto}</strong> ({clienteFolio}).
                <br /><em className="text-xs">Los créditos históricos o renovados son de sólo lectura y no admiten recepción de pagos ordinarios.</em>
              </p>

              <div className="table-responsive">
                <table className="table is-striped is-narrow is-fullwidth" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr>
                      <th>Folio</th>
                      <th>Modalidad</th>
                      <th>Estado</th>
                      <th>Monto Original</th>
                      <th>Saldo Restante</th>
                      <th>Condición</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resolucionCliente?.creditosHistoricos?.map((c) => (
                      <tr key={c.id}>
                        <td className="font-mono font-bold">{c.folio}</td>
                        <td>{c.tipoCreditoCanonico || c.tipoCredito}</td>
                        <td><StatusBadge value={c.estado} /></td>
                        <td className="font-mono">{moneda(c.montoSolicitado)}</td>
                        <td className="font-mono">{moneda(c.saldoActual || 0)}</td>
                        <td className="text-muted text-xs">{c.motivoHistorico || 'Histórico'}</td>
                      </tr>
                    ))}
                    {(!resolucionCliente?.creditosHistoricos || resolucionCliente.creditosHistoricos.length === 0) && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted p-3">
                          Sin créditos históricos registrados para este cliente.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setHistorialModalOpen(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comprobante Operativo MIDAS */}
      {comprobanteModal && (
        <ComprobantePagoModal
          datos={comprobanteModal}
          onClose={() => setComprobanteModal(null)}
        />
      )}
    </div>
  );
}

/**
 * Modal de Comprobante Operativo MIDAS
 */
function ComprobantePagoModal({ datos, onClose }) {
  const imprimir = () => {
    window.print();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content comprobante-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Comprobante Operativo de Pago</h2>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="comprobante-ticket" id="printable-receipt">
          <div className="ticket-header">
            <h3>MIDAS SISTEMA FINANCIERO</h3>
            <p className="ticket-subtitle">COMPROBANTE DE PAGO EN CAJA</p>
            <div className="ticket-divider" />
          </div>

          <div className="ticket-body">
            <div className="ticket-row">
              <span>Folio:</span>
              <strong className="font-mono">{datos.folio}</strong>
            </div>
            <div className="ticket-row">
              <span>Fecha / Hora:</span>
              <span>{new Date(datos.timestamp).toLocaleString('es-MX')}</span>
            </div>
            <div className="ticket-row">
              <span>Crédito:</span>
              <strong className="font-mono">{datos.folioCredito}</strong>
            </div>
            <div className="ticket-row">
              <span>Cliente:</span>
              <strong>{datos.cliente}</strong>
            </div>
            <div className="ticket-row">
              <span>Destino:</span>
              <strong className="font-mono">{datos.destino || 'CUOTA'}</strong>
            </div>
            <div className="ticket-row">
              <span>Forma de Pago:</span>
              <span>{datos.forma}</span>
            </div>
            <div className="ticket-row is-highlight">
              <span>Importe Recibido:</span>
              <strong className="ticket-amount font-mono">{moneda(datos.importe)}</strong>
            </div>

            <div className="ticket-divider" />
            <span className="ticket-section-title">Aplicación a Cuenta:</span>
            {datos.aplicaciones?.map((a, i) => (
              <div key={i} className="ticket-app-row">
                <span>
                  {a.tipo === 'MORATORIO' ? `Moratorio cuota #${a.numeroPago}` : `Cuota #${a.numeroPago}`}
                </span>
                <span className="font-mono">{moneda(a.importeAplicado)}</span>
              </div>
            ))}

            <div className="ticket-divider" />
            <div className="ticket-row">
              <span>Saldo de Cuotas:</span>
              <span className="font-mono">{moneda(datos.saldoCuotasRestante ?? datos.saldoCuotas ?? '0.00')}</span>
            </div>
            <div className="ticket-row">
              <span>Moratorios Pendientes:</span>
              <span className="font-mono">{moneda(datos.saldoMoratoriosRestante ?? datos.saldoMoratorios ?? '0.00')}</span>
            </div>
            <div className="ticket-row is-total-row">
              <span className="font-bold">SALDO TOTAL RESTANTE:</span>
              <strong className="ticket-total font-mono">{moneda(datos.saldoTotalRestante ?? datos.saldoPosterior)}</strong>
            </div>
            <div className="ticket-row">
              <span>Cajero / Operador:</span>
              <span>{datos.usuario}</span>
            </div>
          </div>

          <div className="ticket-footer">
            <p>Este comprobante es un documento operativo de control interno MIDAS.</p>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cerrar
          </button>
          <button type="button" className="btn btn-primary" onClick={imprimir}>
            <Printer size={16} />
            <span>Imprimir Ticket</span>
          </button>
        </div>
      </div>
    </div>
  );
}
